import axios from 'axios'

/**
 * Convert base64 string to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Check if the browser supports push notifications
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window
}

/**
 * Request permission for push notifications
 */
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    return {granted: false, reason: 'browser_not_supported'}
  }

  try {
    const permission = await Notification.requestPermission()
    return {granted: permission === 'granted', permission}
  } catch (error) {
    console.error('Error requesting notification permission:', error)
    return {granted: false, reason: 'permission_failed', error}
  }
}

/**
 * Get the current notification permission state
 */
export function getNotificationPermission() {
  if (!isPushSupported()) {
    return {granted: false, reason: 'browser_not_supported'}
  }

  return {
    granted: Notification.permission === 'granted',
    permission: Notification.permission
  }
}

/**
 * Ensure service worker is registered and get the registration
 * This is critical for environments where the service worker might not be ready yet
 */
async function ensureServiceWorkerRegistration() {
  // If we already have a registration in navigator.serviceWorker.ready, use that
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Service worker not ready')), 1000))
    ])
    return registration
  } catch (error) {
    console.log('ServiceWorker not ready, attempting to register', error)

    // If service worker isn't ready, try to register it manually
    try {
      console.log('Registering service worker manually...')
      const baseUrl = process.env.BASE_URL || '/'
      const registration = await navigator.serviceWorker.register(`${baseUrl}sworker.js`)

      // Wait for the service worker to be activated
      if (registration.installing || registration.waiting) {
        await new Promise(resolve => {
          registration.addEventListener('statechange', () => {
            if (registration.active) {
              resolve()
            }
          })
        })
      }

      console.log('Service worker registered successfully')
      return registration
    } catch (registrationError) {
      console.error('Failed to register service worker', registrationError)
      throw registrationError
    }
  }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPushNotifications() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported')
  }

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      throw new Error('Notification permission denied')
    }
  }

  try {
    // Ensure service worker is registered
    const registration = await ensureServiceWorkerRegistration()
    console.log('Service worker registration ready:', registration)

    // Get public key from server
    const response = await axios.get('/api/v1/push/key')
    const vapidPublicKey = response.data.publicKey

    if (!vapidPublicKey) {
      throw new Error('No VAPID public key available')
    }

    // Convert the key
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey)

    // Unsubscribe from any existing subscriptions
    const existingSubscription = await registration.pushManager.getSubscription()
    if (existingSubscription) {
      await existingSubscription.unsubscribe()
    }

    // Create a new subscription
    console.log('Subscribing to push notifications...')
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    })

    // Get device type (simplified, you might want to improve this detection)
    const userAgent = navigator.userAgent || navigator.vendor || window.opera
    let deviceType = 'web'

    if (/android/i.test(userAgent)) {
      deviceType = 'android'
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      deviceType = 'ios'
    }

    // Send the subscription to your server
    await axios.post('/api/v1/push/subscribe', {
      subscription: subscription.toJSON(),
      deviceType
    })

    console.log('Push subscription successful')
    return {success: true, subscription}
  } catch (error) {
    console.error('Error subscribing to push notifications:', error)
    throw error
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications() {
  if (!isPushSupported()) {
    return {success: false, reason: 'not_supported'}
  }

  try {
    // Ensure service worker is registered
    const registration = await ensureServiceWorkerRegistration()
    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      return {success: true, reason: 'not_subscribed'}
    }

    // Send unsubscribe request to server
    await axios.post('/api/v1/push/unsubscribe', {
      endpoint: subscription.endpoint
    })

    // Unsubscribe locally
    await subscription.unsubscribe()

    return {success: true}
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error)
    return {success: false, error: error.message}
  }
}

/**
 * Check if the user is currently subscribed to push notifications
 */
export async function checkPushSubscription() {
  if (!isPushSupported()) {
    return {subscribed: false, reason: 'not_supported'}
  }

  try {
    // Ensure service worker is registered
    const registration = await ensureServiceWorkerRegistration()
    const subscription = await registration.pushManager.getSubscription()

    return {
      subscribed: !!subscription,
      subscription: subscription ? subscription.toJSON() : null
    }
  } catch (error) {
    console.error('Error checking push subscription:', error)
    return {subscribed: false, error: error.message}
  }
}
